import { html } from "lit";
import type { RouteConfig, BaseRouteConfig } from "@lit-labs/router";
import { pb } from "./api/client.js";
import { navigate } from "./nav.js";

// Register every page custom element (side-effect imports).
import "./pages/page-search.js";
import "./pages/page-package.js";
import "./pages/page-version.js";
import "./pages/page-team.js";
import "./pages/page-teams.js";
import "./pages/page-scopes.js";
import "./pages/page-scope-deleted.js";
import "./pages/page-login.js";
import "./pages/page-signup.js";
import "./pages/page-welcome.js";
import "./pages/page-settings.js";
import "./pages/page-not-found.js";
import { hasHandle } from "./api/account.js";

/**
 * guard is the auth gate for the signed-in routes (R19/S10). Anonymous users are
 * redirected to /login?next=<attempted path>; browse/detail stay public. An
 * authenticated user who has not finished onboarding (no handle yet) is routed to
 * /welcome to claim one before any gated route. Returning false cancels the
 * in-progress navigation (the redirect drives the new render).
 */
function guard(): boolean {
  if (!pb.authStore.isValid) {
    navigate("/login?next=" + encodeURIComponent(location.pathname + location.search), {
      replace: true,
    });
    return false;
  }
  if (!hasHandle() && location.pathname !== "/welcome") {
    navigate("/welcome", { replace: true });
    return false;
  }
  return true;
}

// The route table (R5). Order is not significant — @lit-labs/router matches the
// full pathname via URLPattern — but specific paths are listed before the bare
// package path for readability.
export const routes: RouteConfig[] = [
  { path: "/", render: () => html`<page-search .search=${location.search}></page-search>` },
  { path: "/login", render: () => html`<page-login></page-login>` },
  { path: "/signup", render: () => html`<page-signup></page-signup>` },
  { path: "/welcome", enter: guard, render: () => html`<page-welcome></page-welcome>` },
  { path: "/settings", enter: guard, render: () => html`<page-settings></page-settings>` },
  {
    path: "/settings/:section",
    enter: guard,
    render: ({ section }) => html`<page-settings .section=${section ?? "profile"}></page-settings>`,
  },
  {
    path: "/p/:scope/:name/v/:version",
    render: ({ scope, name, version }) =>
      html`<page-version .scope=${scope ?? ""} .name=${name ?? ""} .version=${version ?? ""}></page-version>`,
  },
  {
    path: "/p/:scope/:name",
    render: ({ scope, name }) =>
      html`<page-package .scope=${scope ?? ""} .name=${name ?? ""}></page-package>`,
  },
  { path: "/teams", enter: guard, render: () => html`<page-teams></page-teams>` },
  {
    path: "/teams/:slug",
    enter: guard,
    render: ({ slug }) => html`<page-team .slug=${slug ?? ""}></page-team>`,
  },
  {
    path: "/scopes/:scope/deleted",
    enter: guard,
    render: ({ scope }) => html`<page-scope-deleted .scope=${scope ?? ""}></page-scope-deleted>`,
  },
  {
    path: "/scopes/:scope",
    render: ({ scope }) => html`<page-scopes .scope=${scope ?? ""}></page-scopes>`,
  },
];

export const fallback: BaseRouteConfig = {
  render: () => html`<page-not-found></page-not-found>`,
};
